import React, { useCallback, useState, createContext } from 'react';
import { useTranslation } from 'react-i18next';
import AlertSnackbar from '@/components/UI/AlertSnackbar/AlertSnackbar';
import styles from '@/components/UI/AlertSnackbar/AlertSnackbar.module.scss';
import { SUCCESS, MAX_NOTIFICATIONS, ERROR } from '@/helpers/constants';
import { AlertSnackbarSeverities } from '@/types';

// @ts-expect-error:next-line
const ToastContext = createContext();
const toastTimeouts = {};

export default ToastContext;

type Toast = {
  id: string;
  text: string;
  type: AlertSnackbarSeverities;
};

export function ToastContextProvider({ children }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const { t } = useTranslation();

  const addToast = useCallback(
    (text: string, type: AlertSnackbarSeverities = SUCCESS, uniqueKey: string | undefined = undefined) => {
      const id = uniqueKey || type === ERROR ? text : `${text}${(new Date()).getTime()}`;
      const toast = { id, text, type };

      if (uniqueKey || type === ERROR) setToasts((otherToasts) => [...otherToasts.filter((tost) => tost.id !== id), toast]);
      else setToasts((otherToasts) => [...otherToasts, toast]);

      if (toast.type !== 'Fixed') {
        clearTimeout(toastTimeouts?.[id]);
        toastTimeouts[id] = setTimeout(() => setToasts((allToasts) => allToasts.filter((to) => to.id !== id)), 40000);
      }
    }, [setToasts, toastTimeouts], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const close = (id: string) => {
    const filteredToasts = toasts.filter((to) => to.id !== id);
    setToasts(filteredToasts);
  };

  const closeValidationToast = () => {
    const filteredToasts = toasts.filter((to) => to.text !== t('alert.fourEyesInvalid')
      && to.text !== t('alert.mandatoryFieldsEmpty') && to.text !== t('alert.allVehiclesInGroups'));
    setToasts(filteredToasts);
  };

  const closeToasts = (type) => {
    const filteredToasts = toasts.filter((to) => to.type !== type);
    setToasts(filteredToasts);
  };

  const numberOfToastsVisible = toasts.length;

  return (
    <ToastContext.Provider value={{ addToast, closeValidationToast, closeToasts, numberOfToastsVisible }}>
      {children}
      <div className={styles.Notifications}>
        {toasts.slice(-MAX_NOTIFICATIONS).map((toast) => (
          <AlertSnackbar
            key={toast.id}
            id={toast.id}
            open
            severity={toast.type}
            onClose={() => close(toast.id)}
          >
            {toast.text}
          </AlertSnackbar>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
